import { useMemo, useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Paper from '@mui/material/Paper'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useDashboard, useRevenueSeries } from '../shared/api/dashboard'
import { useOwnerAuth } from './OwnerAuthContext'
import { useShopSettings } from '../shared/api/shop'
import { formatMoney, formatKg } from '../shared/money'
import RevenueChart, { type ChartMetric } from './RevenueChart'
import type { RevenueGranularity } from '../shared/types'

const DAY_RANGES = [7, 30, 90]
const MONTH_RANGES = [6, 12]

/** Local date → `yyyy-MM-dd` (the storekeeper's browser zone ≈ the server's business zone). */
function isoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Derives the from/to ISO dates for a granularity + span (e.g. last 30 days, last 12 months). */
function computeRange(granularity: RevenueGranularity, span: number): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today)
  if (granularity === 'MONTH') {
    start.setMonth(start.getMonth() - (span - 1))
    start.setDate(1)
  } else {
    start.setDate(start.getDate() - (span - 1))
  }
  return { from: isoDate(start), to: isoDate(today) }
}

/** Maps a span number to its i18n range key. */
function rangeKey(granularity: RevenueGranularity, n: number): string {
  if (granularity === 'MONTH') return n === 6 ? 'last6m' : 'last12m'
  return n === 7 ? 'last7' : n === 90 ? 'last90' : 'last30'
}

/** OWNER-only analytics dashboard (D-051): summary cards, top sellers, and a customizable revenue chart. */
export default function DashboardPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useOwnerAuth()
  const { data, isLoading, isError } = useDashboard(isAuthenticated)
  // Priceless shop (D-069): orders have no totals, so revenue is meaningless — hide it, keep counts.
  const { data: settings } = useShopSettings(isAuthenticated)
  const showPrices = settings?.showPrices ?? true

  const [granularity, setGranularity] = useState<RevenueGranularity>('DAY')
  const [dayRange, setDayRange] = useState(30)
  const [monthRange, setMonthRange] = useState(12)
  const span = granularity === 'MONTH' ? monthRange : dayRange
  const { from, to } = useMemo(() => computeRange(granularity, span), [granularity, span])
  const series = useRevenueSeries(granularity, from, to, isAuthenticated)

  const currency = data?.currency ?? 'INR'
  // Show kg cards + a kg chart when this shop actually sells by weight (D-099) — a chicken shop's
  // volume metric, and the natural stand-in for revenue on a priceless shop (D-069).
  const hasWeight = !!data && data.total.weight > 0
  const chartMetric: ChartMetric = showPrices ? 'revenue' : hasWeight ? 'weight' : 'orders'
  const chartTitleKey =
    chartMetric === 'revenue' ? 'revenueTrend' : chartMetric === 'weight' ? 'weightTrend' : 'ordersTrend'

  const cards = data
    ? [
        { key: 'ordersToday', value: String(data.today.orders) },
        ...(showPrices ? [{ key: 'revenueToday', value: formatMoney(data.today.revenue, currency) }] : []),
        ...(hasWeight ? [{ key: 'kgToday', value: formatKg(data.today.weight) }] : []),
        { key: 'totalOrders', value: String(data.total.orders) },
        ...(showPrices ? [{ key: 'totalRevenue', value: formatMoney(data.total.revenue, currency) }] : []),
        ...(hasWeight ? [{ key: 'kgTotal', value: formatKg(data.total.weight) }] : []),
        { key: 'ordersThisMonth', value: String(data.month.orders) },
        ...(showPrices ? [{ key: 'revenueThisWeek', value: formatMoney(data.week.revenue, currency) }] : []),
        ...(hasWeight ? [{ key: 'kgThisWeek', value: formatKg(data.week.weight) }] : []),
      ]
    : []

  return (
    <Container maxWidth="lg" sx={{ py: 2, pb: 8 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t('owner.dashboard.title')}
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : isError || !data ? (
        <Typography color="error">{t('owner.dashboard.loadError')}</Typography>
      ) : (
        <Stack spacing={2}>
          {/* Summary cards */}
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
            }}
          >
            {cards.map((c) => (
              <Card key={c.key} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {t(`owner.dashboard.${c.key}`)}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }} noWrap>
                    {c.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Trend chart + controls. A priceless shop (D-069) has no revenue to plot, so it charts
              kg sold (catch-weight shops) or order count instead — the chart is never just hidden. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t(`owner.dashboard.${chartTitleKey}`)}
              </Typography>
              <Stack direction="row" spacing={1}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={granularity}
                  onChange={(_, v) => v && setGranularity(v as RevenueGranularity)}
                >
                  <ToggleButton value="DAY">{t('owner.dashboard.granularity.DAY')}</ToggleButton>
                  <ToggleButton value="MONTH">{t('owner.dashboard.granularity.MONTH')}</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  select
                  size="small"
                  value={span}
                  onChange={(e) =>
                    granularity === 'MONTH'
                      ? setMonthRange(Number(e.target.value))
                      : setDayRange(Number(e.target.value))
                  }
                  sx={{ minWidth: 150 }}
                >
                  {(granularity === 'MONTH' ? MONTH_RANGES : DAY_RANGES).map((n) => (
                    <MenuItem key={n} value={n}>
                      {t(`owner.dashboard.range.${rangeKey(granularity, n)}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
            {series.isLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : series.isError || !series.data ? (
              <Typography color="error">{t('owner.dashboard.loadError')}</Typography>
            ) : (
              <RevenueChart series={series.data} metric={chartMetric} />
            )}
          </Paper>

          {/* Top sellers */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t('owner.dashboard.topItems')}
            </Typography>
            {data.topItems.length === 0 ? (
              <Typography color="text.secondary">{t('owner.dashboard.noSales')}</Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={1}>
                {data.topItems.map((item, idx) => (
                  <Stack key={item.productId} direction="row" alignItems="center" spacing={1.5}>
                    <Typography color="text.secondary" sx={{ width: 20, textAlign: 'right' }}>
                      {idx + 1}
                    </Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography noWrap fontWeight={600}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('owner.dashboard.qtySold', { count: item.quantity })}
                      </Typography>
                    </Box>
                    {showPrices && <Typography fontWeight={700}>{formatMoney(item.revenue, currency)}</Typography>}
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}
    </Container>
  )
}
